import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './FlowLayout.css';
import './Payment.css';

function Payment() {
  var navigate = useNavigate();
  var [cardNumber, setCardNumber] = useState('');
  var [expiry, setExpiry] = useState('');
  var [cvc, setCvc] = useState('');
  var [name, setName] = useState('');
  var [zip, setZip] = useState('');
  var [processing, setProcessing] = useState(false);

  var locationName = localStorage.getItem('selectedLocationName') || 'Your selected enrollment center';

  function formatCardNumber(val) {
    var digits = val.replace(/\D/g, '').slice(0, 16);
    var groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : '';
  }

  function formatExpiry(val) {
    var digits = val.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) {
      return digits.slice(0, 2) + ' / ' + digits.slice(2);
    }
    return digits;
  }

  var isValid = cardNumber.replace(/\s/g, '').length === 16 &&
    expiry.replace(/\D/g, '').length === 4 &&
    cvc.length >= 3 &&
    name.length > 0 &&
    zip.length >= 5;

  function handlePay() {
    if (!isValid || processing) return;
    setProcessing(true);
    // Simulate Stripe processing
    setTimeout(function() {
      navigate('/confirmed2');
      window.scrollTo(0, 0);
    }, 1500);
  }

  return (
    <div className="flow-page">
      <Helmet>
        <title>Payment</title>
        <meta name="description" content="Complete your Easy Global Entry autobook payment." />
      </Helmet>

      {/* Progress Bar */}
      <div className="progress-bar-container" role="progressbar" aria-valuenow={4} aria-valuemin={1} aria-valuemax={5} aria-label="Step 4 of 5: Payment">
        <div className="progress-bar-header">
          <button className="flow-back" onClick={function() { navigate('/connect'); window.scrollTo(0, 0); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <div className="progress-bar-label">Step 4 of 5: Payment</div>
        </div>
        <div className="progress-bar-track">
          <div className="progress-bar-fill" style={{ width: '80%' }} />
        </div>
      </div>

      {/* Split layout */}
      <div className="flow-layout">
        {/* Left column — order summary */}
        <div className="flow-left">
          <h1 className="flow-heading">Complete your purchase</h1>
          <p className="flow-subtitle" style={{ marginBottom: '32px' }}>
            You're almost there. Review your order and enter payment details.
          </p>

          {/* Order summary card */}
          <div className="pay-summary">
            <div className="pay-summary__row">
              <span className="pay-summary__label">Plan</span>
              <span className="pay-summary__value">Autobook</span>
            </div>
            <div className="pay-summary__divider"></div>
            <div className="pay-summary__row">
              <span className="pay-summary__label">Location</span>
              <span className="pay-summary__value">{locationName}</span>
            </div>
            <div className="pay-summary__divider"></div>
            <div className="pay-summary__row pay-summary__row--total">
              <span className="pay-summary__label">Total</span>
              <span className="pay-summary__total">$25.00</span>
            </div>
          </div>

          <div className="pay-guarantee">
            <svg viewBox="0 0 24 24" fill="none" stroke="#2E8540" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Full refund if we can't book your appointment</span>
          </div>
        </div>

        {/* Right column — payment form */}
        <div className="flow-right">
          <div className="panel-card">
            {/* Stripe-like header */}
            <div className="pay-header">
              <span className="pay-header__text">Payment details</span>
              <div className="pay-header__badges">
                <span className="pay-badge">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#5B7083" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Secure
                </span>
              </div>
            </div>

            {/* Card number */}
            <div className="pay-field">
              <label className="flow-label" htmlFor="card-number">Card Number</label>
              <input
                id="card-number"
                className="connect-input"
                type="text"
                inputMode="numeric"
                placeholder="1234 5678 9012 3456"
                value={cardNumber}
                onChange={function(e) { setCardNumber(formatCardNumber(e.target.value)); }}
              />
            </div>

            {/* Expiry + CVC row */}
            <div className="pay-row">
              <div className="pay-field pay-field--half">
                <label className="flow-label" htmlFor="card-expiry">Expiration</label>
                <input
                  id="card-expiry"
                  className="connect-input"
                  type="text"
                  inputMode="numeric"
                  placeholder="MM / YY"
                  value={expiry}
                  onChange={function(e) { setExpiry(formatExpiry(e.target.value)); }}
                />
              </div>
              <div className="pay-field pay-field--half">
                <label className="flow-label" htmlFor="card-cvc">CVC</label>
                <input
                  id="card-cvc"
                  className="connect-input"
                  type="text"
                  inputMode="numeric"
                  maxLength="4"
                  placeholder="123"
                  value={cvc}
                  onChange={function(e) { setCvc(e.target.value.replace(/\D/g, '').slice(0, 4)); }}
                />
              </div>
            </div>

            {/* Name on card */}
            <div className="pay-field">
              <label className="flow-label" htmlFor="card-name">Name on Card</label>
              <input
                id="card-name"
                className="connect-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={function(e) { setName(e.target.value); }}
              />
            </div>

            {/* Zip */}
            <div className="pay-field">
              <label className="flow-label" htmlFor="card-zip">Billing Zip Code</label>
              <input
                id="card-zip"
                className="connect-input"
                type="text"
                inputMode="numeric"
                maxLength="5"
                placeholder="00000"
                value={zip}
                onChange={function(e) { setZip(e.target.value.replace(/\D/g, '').slice(0, 5)); }}
                onKeyDown={function(e) { if (e.key === 'Enter') handlePay(); }}
              />
            </div>

            {/* Pay button */}
            <button
              className="flow-btn-primary pay-btn"
              onClick={handlePay}
              disabled={!isValid || processing}
              style={{ width: '100%' }}
            >
              {processing ? (
                <span className="pay-btn__processing">
                  <span className="pay-spinner"></span>
                  Processing...
                </span>
              ) : (
                'Pay $25.00'
              )}
            </button>

            <div className="pay-powered">
              <span>Powered by</span>
              <span className="pay-powered__stripe">Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Payment;
